import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, TrendingUp, ThumbsUp } from 'lucide-react';
import ReviewCard from './UserReviewsComponents/ReviewCard';
import { ReviewProvider, useReviewContext } from '../../context/ReviewContext';

const UserReviewsContent = () => {
  const { reviews, loading, error, getReviews } = useReviewContext();
  const [businessId, setBusinessId] = useState('');
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token_partner_shop');
    const id = localStorage.getItem('id_partner_shop');

    if (token && id) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const bId = payload.business_id || id;
        setBusinessId(bId);
        getReviews('Shop', bId, token);
      } catch (e) {
        setAuthError('Error parsing authentication info');
      }
    } else {
      setAuthError('Authentication required. Please login again.');
    }
  }, [getReviews]);

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const avgRating = calculateAverageRating();

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-gray-500 font-medium animate-pulse uppercase text-[10px] tracking-widest">Loading feedback...</p>
      </div>
    );

  if (authError || error) return (
    <div className="p-12 text-center uppercase text-[10px] font-black tracking-widest">
      <div className="p-4 bg-rose-50 text-rose-500 rounded-3xl inline-block">
        {authError || error}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-10 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-primary tracking-tight">Customer Feedback</h1>
          <p className="text-gray-500 font-medium mt-1">Review and manage your store's reputation.</p>
        </div>

        <div className="flex items-center gap-4 bg-white/60 backdrop-blur-xl px-8 py-5 rounded-[2rem] border border-white/50 shadow-sm transition-all hover:shadow-md">
          <div className="text-center border-r border-gray-100 pr-6">
            <p className="text-3xl font-black text-gray-900 leading-none tracking-tighter">{avgRating}</p>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Rating</p>
          </div>
          <div className="pl-2">
            <div className="flex gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-3 h-3 ${star <= Math.round(avgRating)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-gray-200'
                    }`}
                />
              ))}
            </div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Global score</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-primary to-emerald-500 p-8 rounded-[2.5rem] text-white shadow-xl shadow-primary/20 flex items-center justify-between group overflow-hidden relative">
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Total Reviews</p>
            <p className="text-4xl font-black mt-1 tracking-tighter">{reviews.length}</p>
          </div>
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md relative z-10">
            <MessageSquare size={28} />
          </div>
          <div className="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
        </div>

        <div className="bg-white/60 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/50 shadow-sm flex items-center justify-between group">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Growth Index</p>
            <p className="text-4xl font-black text-gray-900 mt-1 tracking-tighter">+{Math.min(reviews.length, 12)}%</p>
          </div>
          <div className="w-14 h-14 bg-emerald-500/10 text-emerald-500 rounded-2xl flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-all duration-500">
            <TrendingUp size={28} />
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/50 shadow-sm flex items-center justify-between group">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Satisfaction</p>
            <p className="text-4xl font-black text-gray-900 mt-1 tracking-tighter">{Math.round((avgRating / 5) * 100)}%</p>
          </div>
          <div className="w-14 h-14 bg-amber-500/10 text-amber-500 rounded-2xl flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-all duration-500">
            <ThumbsUp size={28} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-12">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <ReviewCard key={review._id} review={review} />
          ))
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center py-32 bg-white/40 backdrop-blur-sm rounded-[3rem] border border-dashed border-gray-200">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-6 font-black tracking-tighter uppercase text-[10px]">
              Empty
            </div>
            <h3 className="text-xl font-bold text-gray-800 tracking-tight lowercase first-letter:uppercase">No reviews yet</h3>
            <p className="text-gray-500 text-sm max-w-xs text-center mt-2 font-medium lowercase first-letter:uppercase">Your customers haven't shared their feedback yet. Your reviews will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const UserReviews = () => (
  <ReviewProvider>
    <UserReviewsContent />
  </ReviewProvider>
);

export default UserReviews;
